/**
 * Input Interaction Manager
 * 
 * Handles all input callbacks and interaction logic for OceanScene.
 * Manages island/whirlpool/rift right-clicks, context menus, and click handlers.
 */

import Phaser from 'phaser';
import { Socket } from 'socket.io-client';
import { TILE_SIZE, IslandData } from '../types';
import { FishingSpotInfo } from '../systems/FishingSpotRenderer';
import { WhirlpoolEntryData } from '../systems/WhirlpoolRenderer';

export interface InputInteractionCallbacks {
  getLocalShip: () => Phaser.GameObjects.Container | null;
  getLocalPlayerId: () => string;
  getSocket: () => Socket | null;
  getIslandAtPosition: (worldX: number, worldY: number) => IslandData | null;
  getRiftScarAtPosition: (worldX: number, worldY: number) => any | null;
  canDockAtIsland: (island: IslandData) => boolean;
  isPlayerDocked: () => boolean;
  getDockedIslandId: () => string | undefined;
  moveToPosition: (worldX: number, worldY: number) => void;
  disengageTarget: () => void;
  dockAtIsland: (islandId: string) => void;
  undockFromIsland: () => void;
  startFishing: (spot: FishingSpotInfo) => void;
  showFloatingMessage: (message: string, color: string) => void;
}

export class InputInteractionManager {
  private scene: Phaser.Scene;
  private callbacks: InputInteractionCallbacks;
  private pendingFishingSpot: FishingSpotInfo | null = null;

  constructor(scene: Phaser.Scene, callbacks: InputInteractionCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  // ============ WHIRLPOOL INTERACTIONS ============

  /**
   * Handle Whirlpool click - move to whirlpool and enter
   */
  handleWhirlpoolClick(entry: WhirlpoolEntryData): void {
    const localShip = this.callbacks.getLocalShip();
    if (!localShip) return;
    
    const dx = localShip.x - entry.worldX;
    const dy = localShip.y - entry.worldY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance <= 50) {
      const socket = this.callbacks.getSocket();
      socket?.emit('whirlpool:enter', { entryId: entry.id });
    } else {
      this.callbacks.disengageTarget();
      this.callbacks.moveToPosition(entry.worldX, entry.worldY);
      this.scene.events.emit('whirlpool:approaching', { entry });
    }
  }

  /**
   * Handle Whirlpool exit click
   */
  handleWhirlpoolExitClick(worldX: number, worldY: number, isInWhirlpoolInstance: boolean): void {
    const localShip = this.callbacks.getLocalShip();
    const socket = this.callbacks.getSocket();
    if (!localShip || !socket || !isInWhirlpoolInstance) return;

    const dist = Math.hypot(localShip.x - worldX, localShip.y - worldY);

    if (dist <= 50) {
      socket.emit('whirlpool:exit');
      return;
    }

    this.callbacks.disengageTarget();
    this.callbacks.moveToPosition(worldX, worldY);
  }

  /**
   * Handle Whirlpool right-click - emit context menu event
   */
  handleWhirlpoolRightClick(entry: WhirlpoolEntryData, screenX: number, screenY: number): void {
    this.scene.events.emit('whirlpool:contextmenu', {
      whirlpoolId: entry.id,
      whirlpoolPosition: { x: entry.worldX, y: entry.worldY },
      whirlpoolClass: entry.class,
      screenX,
      screenY,
    });
  }

  // ============ ISLAND INTERACTIONS ============

  /**
   * Handle Trading Post click - dock at location
   */
  handleTradingPostClick(island: IslandData, _worldX: number, _worldY: number): void {
    const localShip = this.callbacks.getLocalShip() as any;
    if (!localShip) return;
    
    console.log(`🏪 Trading Post clicked: ${island.permanentName}`);
    
    this.callbacks.disengageTarget();
    
    if (localShip.shipData?.combatState === 'docked' && localShip.shipData?.dockedAt !== island.id) {
      this.callbacks.undockFromIsland();
    }
    
    this.callbacks.dockAtIsland(island.id);
  }

  /**
   * Handle Shipyard click - dock at location
   */
  handleShipyardClick(island: IslandData, _worldX: number, _worldY: number): void {
    const localShip = this.callbacks.getLocalShip() as any;
    if (!localShip) return;
    
    console.log(`⚓ Shipyard clicked: ${island.permanentName}`);
    
    this.callbacks.disengageTarget();

    if (localShip.shipData?.combatState === 'docked' && localShip.shipData?.dockedAt !== island.id) {
      this.callbacks.undockFromIsland();
    }

    this.callbacks.dockAtIsland(island.id);
  }

  /**
   * Handle Harbor Office click - dock at location
   */
  handleHarborOfficeClick(island: IslandData, _worldX: number, _worldY: number): void {
    const localShip = this.callbacks.getLocalShip() as any;
    if (!localShip) return;

    console.log(`🏛️ Harbor Office clicked: ${island.permanentName}`);

    this.callbacks.disengageTarget();

    if (localShip.shipData?.combatState === 'docked' && localShip.shipData?.dockedAt !== island.id) {
      this.callbacks.undockFromIsland();
    }

    this.callbacks.dockAtIsland(island.id);
  }

  /**
   * Handle Tavern click - dock at location
   */
  handleTavernClick(island: IslandData, _worldX: number, _worldY: number): void {
    const localShip = this.callbacks.getLocalShip() as any;
    if (!localShip) return;

    console.log(`🍺 Tavern clicked: ${island.permanentName}`);

    this.callbacks.disengageTarget();

    if (localShip.shipData?.combatState === 'docked' && localShip.shipData?.dockedAt !== island.id) {
      this.callbacks.undockFromIsland();
    }

    this.callbacks.dockAtIsland(island.id);
  }

  /**
   * Handle island right-click - emit event to show context menu
   */
  handleIslandRightClick(island: IslandData, screenX: number, screenY: number): void {
    console.log(`Island right-clicked: ${island.permanentName || island.id}`);
    
    const canDock = this.callbacks.canDockAtIsland(island);
    const isDocked = this.callbacks.isPlayerDocked();
    const isDockedHere = this.callbacks.getDockedIslandId() === island.id;
    
    console.log(`🎯 OceanScene: Emitting island:research event for ${island.permanentName || island.id}`);
    this.scene.events.emit('island:research', {
      islandId: island.id,
      islandPosition: { x: island.x, y: island.y },
      screenPosition: { x: screenX, y: screenY },
      canDock,
      isDocked,
      isDockedHere,
      isPermanent: island.isPermanent,
      permanentName: island.permanentName,
    });
  }

  // ============ RIFT SCAR INTERACTIONS ============

  /**
   * Handle right-click on rift scar - show context menu
   */
  handleRiftScarRightClick(scarData: any, screenX: number, screenY: number): void {
    console.log(`🌀 OceanScene: Right-clicked rift scar: ${scarData.name}`);
    this.scene.events.emit('riftscar:contextmenu', {
      scarData,
      screenX,
      screenY,
    });
  }

  // ============ FISHING SPOT INTERACTIONS ============

  /**
   * Handle fishing spot right-click - emit event to show research panel
   */
  handleFishingSpotRightClick(spot: FishingSpotInfo, screenX: number, screenY: number): void {
    console.log(`🔍 Fishing spot right-clicked: ${spot.type} at (${spot.tileX}, ${spot.tileY})`);
    this.scene.events.emit('fishingspot:research', {
      spotId: spot.id,
      spotType: spot.type,
      spotPosition: { x: spot.worldX, y: spot.worldY },
      tilePosition: { x: spot.tileX, y: spot.tileY },
      screenPosition: { x: screenX, y: screenY },
    });
  }

  /**
   * Handle Fishing Spot click - move to adjacent tile and start fishing
   */
  handleFishingSpotClick(spot: FishingSpotInfo): void {
    const localShip = this.callbacks.getLocalShip();
    if (!localShip) return;
    
    const playerTileX = Math.floor(localShip.x / TILE_SIZE);
    const playerTileY = Math.floor(localShip.y / TILE_SIZE);
    
    const distX = Math.abs(playerTileX - spot.tileX);
    const distY = Math.abs(playerTileY - spot.tileY);
    const isAdjacent = distX <= 1 && distY <= 1 && !(distX === 0 && distY === 0);
    
    if (isAdjacent) {
      this.callbacks.startFishing(spot);
    } else {
      const adjacentTiles = [
        { x: spot.tileX - 1, y: spot.tileY },
        { x: spot.tileX + 1, y: spot.tileY },
        { x: spot.tileX, y: spot.tileY - 1 },
        { x: spot.tileX, y: spot.tileY + 1 },
      ];
      
      let closestTile = adjacentTiles[0];
      let closestDist = Infinity;
      
      for (const tile of adjacentTiles) {
        const dist = Math.abs(tile.x - playerTileX) + Math.abs(tile.y - playerTileY);
        if (dist < closestDist) {
          closestDist = dist;
          closestTile = tile;
        }
      }
      
      const targetX = closestTile.x * TILE_SIZE + TILE_SIZE / 2;
      const targetY = closestTile.y * TILE_SIZE + TILE_SIZE / 2;
      
      this.callbacks.disengageTarget();
      this.callbacks.moveToPosition(targetX, targetY);
      
      this.pendingFishingSpot = spot;
    }
  }

  /**
   * Get pending fishing spot (for arrival detection)
   */
  getPendingFishingSpot(): FishingSpotInfo | null {
    return this.pendingFishingSpot;
  }

  /**
   * Clear pending fishing spot
   */
  clearPendingFishingSpot(): void {
    this.pendingFishingSpot = null;
  }

  // ============ CONTEXT MENU HELPERS ============

  /**
   * Check if there is an island under the given world coordinates
   */
  hasIslandAtPosition(worldX: number, worldY: number): boolean {
    return this.callbacks.getIslandAtPosition(worldX, worldY) !== null;
  }

  /**
   * Check if there is a rift scar under the given world coordinates
   */
  hasRiftScarAtPosition(worldX: number, worldY: number): boolean {
    return this.callbacks.getRiftScarAtPosition(worldX, worldY) !== null;
  }

  /**
   * Get the island data at the given world coordinates
   */
  getIslandAtPosition(worldX: number, worldY: number): IslandData | null {
    return this.callbacks.getIslandAtPosition(worldX, worldY);
  }

  /**
   * Get the rift scar at the given world coordinates
   */
  getRiftScarAtPosition(worldX: number, worldY: number): any | null {
    return this.callbacks.getRiftScarAtPosition(worldX, worldY);
  }
}
